import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Router } from '@angular/router';
import { DocumentCategoryViewModel } from 'src/app/modules/shared/viewModels/documents/documentCategoryViewModel';
import { UploadViewModel } from 'src/app/modules/shared/viewModels/documents/uploadViewModel';
import { IncidentsManager } from 'src/app/modules/shared/managers/incidents.manager';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { IncidentsService } from 'src/app/modules/shared/services/incidents.service';
import { DocumentService } from 'src/app/modules/shared/services/document.service';
import { DocumentCategoryService } from 'src/app/modules/shared/services/documentCategory.service';
import { CachingService } from 'src/app/modules/shared/services/caching.service';
import { AccountSettingsService } from 'src/app/modules/shared/services/account-settings.service';
import { IncidentSubTypes } from 'src/app/modules/incidents/enums/incidentSubTypes';
import { Employee } from 'src/app/modules/shared/models/employee';
import { SeverityViewModel } from 'src/app/modules/shared/models/severityViewModel';
import { ZoneViewModel } from 'src/app/modules/shared/viewModels/zoneViewModel';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { PrivacyStatuses } from 'src/app/modules/shared/enums/privacyStatuses';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { LocationViewModel } from 'src/app/modules/shared/viewModels/locationViewModel';
import { IDropdownState } from 'src/app/modules/shared/models/interfaces/dropdown-state.interface';
import { EmployeeViewModel } from 'src/app/modules/shared/viewModels/employeeViewModel';
import { IncidentItemTypes } from 'src/app/modules/incidents/enums/incidentItemTypes';
import { IncidentStatuses } from 'src/app/modules/incidents/enums/incidentStatuses';
import { Account } from 'src/app/modules/shared/models/account';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { IncidentDetailsItemViewModel } from 'src/app/modules/incidents/viewModels/incidentDetailsItemViewModel';
import { AreaViewModel } from 'src/app/modules/settings/viewModels/areaViewModel';
import moment from 'moment';
import { SearchDropdownComponent } from 'src/app/modules/shared/components/rebuild/search-dropdown/search-dropdown.component';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { ConfirmationService } from 'src/app/modules/shared/services/confirmation.service';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { FilterToViewModelUtilityService } from 'src/app/modules/shared/services/utilities/filterToViewModelUtilityService';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { FilterDateOptions } from 'src/app/modules/shared/enums/filter/filterDateOptions';
import { UploadTypes } from 'src/app/modules/shared/enums/uploadTypes';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FixedFilters } from 'src/app/modules/shared/enums/fixedFilters.enum';
import { IncidentMapComponent } from 'src/app/modules/shared/components/common/maps/incident-map/incident-map.component';
import { LocationService } from 'src/app/modules/shared/services/location.service';
import { ObjectEventEmitters } from 'src/app/modules/shared/events/object.events';
import { LocationUtilities } from 'src/app/modules/shared/utilities/location.utilities';
import { TranslateService } from '@ngx-translate/core';
import { T } from 'src/assets/i18n/translation-keys';
import { FixedZoneFiltersService } from 'src/app/modules/shared/services/fixed-zone-filters.service';
import { EnumUtilities } from 'src/app/modules/shared/utilities/enum.utilities';
import { MarkerType } from 'src/app/modules/shared/enums/maps/marketType';
import { IncidentCategoriesService } from 'src/app/modules/settings/services/incidents/incident-categories.service';
import { IncidentCategoryViewModel } from 'src/app/modules/incidents/viewModels/incidentCategoryViewModel';
import { EditableFieldTypes } from 'src/app/modules/shared/enums/editableFieldTypes';
import { AssignMeAsOwnerOrApproverFilterUtilities } from 'src/app/modules/shared/utilities/shared-owner-filter.utilities';
import { ManageExternalUserComponent } from 'src/app/modules/settings/components/common/modals/employee-permissions/manage-external-user/manage-external-user.component';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';

@Component({
    selector: 'app-incident-add-modal',
    templateUrl: './incident-add-modal.component.html',
    styleUrls: ['./incident-add-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IncidentAddModalComponent implements OnInit, OnDestroy {
  @ViewChild('incident-map ') incidentMap: IncidentMapComponent;
  @ViewChild('searchDropdown') searchDropdownComponent: SearchDropdownComponent;
  @Input() incidentSubType: IncidentSubTypes = IncidentSubTypes.General;
  @Output() closed = new EventEmitter();

  // added for PIR linking & event association
  public itemCreated: Subject<{item: IncidentDetailsItemViewModel, objectType: ObjectTypes}> = new Subject();
  public isCalledFromPIR: boolean = false;
  protected copyEventFromPIR: boolean;

  modalHeader = 'Add an Incident';
  currentAccount: Account;
  currentEmployee: Employee;
  incident: IncidentDetailsItemViewModel = new IncidentDetailsItemViewModel();

  severityCheckpoint: SeverityViewModel = this.incidentsManager.getSeverityByValue(1);
  quickAddTitle: string = '';
  showConfidentialStatuses: boolean = false;
  private subscriptions = new Subscription();

  zonesViewModels: ZoneViewModel[] = [];
  areasViewModels: AreaViewModel[] = [];
  mapZones: ZoneViewModel[] = [];
  mapAreas: AreaViewModel[] = [];
  // Enums
  filterTypes = FilterTypes;
  incidentSubTypes: { key: IncidentSubTypes; value: string }[] = EnumUtilities.items(IncidentSubTypes);
  privacyStatuses: { key: PrivacyStatuses; value: string }[] = EnumUtilities.items(PrivacyStatuses);
  privacyStatus: { key: PrivacyStatuses; value: string };
  privacyStatusesEnum = PrivacyStatuses;
  activeEditAreaByFilterTypes: { [key: number]: boolean } = {};
  loading: boolean = false;
  showPrivacyStatus: boolean = true;
  showDueDateAndStartDate: boolean = false;
  documentCategories: DocumentCategoryViewModel[] = [];
  objectType: ObjectTypes = ObjectTypes.IncidentItem;
  editableFieldTypes = EditableFieldTypes;
  // Override form with a pre-populated Incident
  usePopulatedIncident: boolean = false;
  uploadDocumentViewModels: UploadViewModel[] = [];
  useDefaultLocation: boolean = false;
  defaultLocation: LocationViewModel;
  showPickers = false;
  start: string;
  due: string;
  selectedZone: FilterViewModel;
  areaFilters: FilterViewModel[] = [];
  zoneFilters: FilterViewModel[] = [];
  applicableAreaFilters: FilterViewModel[] = [];
  public markerType = MarkerType.INCIDENT;
  public localisedIncident = 'Incident';
  public localisedOwners: string;
  public localisedDepartments: string;
  public localisedZones: string;
  public localisedAreas: string;
  //new
  isTouched: boolean = false;
  public categoryId: number;
  public currentlySelectedExternalUser: FilterViewModel;
  public selectedCategory: IncidentCategoryViewModel;
  public preselectedCategoryId: number;
  public preselectedCategoryFilter: FilterViewModel;
  public copiedEventFilter: FilterViewModel;
  public initialSelectedZone: FilterViewModel;
  public incidentForm: UntypedFormGroup;
  public filters: FilterViewModel[] = [];
  private allowedFilters: FilterViewModel[];
  public validationErrors: string[];
  public triggerErrors: boolean = false;
  public readonly T = T;
  public readonly objectTypes = ObjectTypes;
  private zones: ZoneViewModel[] = [];
  filteredStatuses: { key: PrivacyStatuses; value: string }[];
  public invalidDescription: boolean = false;
  startMaxDate = new Date();
  constructor(
    private readonly incidentCategoriesService: IncidentCategoriesService,
    public readonly incidentsManager: IncidentsManager,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authenticationService: AuthenticationService,
    public readonly bsModalRef: BsModalRef,
    private readonly incidentsService: IncidentsService,
    private readonly router: Router,
    private readonly documentService: DocumentService,
    private readonly documentCategoryService: DocumentCategoryService,
    private readonly cachingService: CachingService,
    private readonly accountSettingsService: AccountSettingsService,
    private readonly localisationService: LocalisationService,
    private readonly confirmationService: ConfirmationService,
    private readonly alertService: AlertService,
    private readonly filterToViewModelUtilityService: FilterToViewModelUtilityService,
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly locationService: LocationService,
    private readonly objectEventEmitters: ObjectEventEmitters,
    private fb: UntypedFormBuilder,
    private readonly translateService: TranslateService,
    private readonly fixedZoneService: FixedZoneFiltersService,
    private readonly wtValidators: WtValidators,
    private readonly modalService: BsModalService,
  ) { }

  ngOnInit() {
    this.loading = true;
    this.closed.emit();

    this.initModalData();

    if (this.filters && this.filters.length > 0) {
      const categoryFilter = this.filters.find(x => x.filterType === FilterTypes.Incident_Category);
      if(categoryFilter) {
        this.preselectedCategoryId = +categoryFilter.filterValue;
      }
    }

    if (this.preselectedCategoryId) {
      this.getSelectedIncidentCategory(this.preselectedCategoryId);

      if(this.preselectedCategoryFilter) this.filters.push(this.preselectedCategoryFilter);
    }

    this.copyEventFromPIR = this.isCalledFromPIR;

    if (this.isCalledFromPIR && this.copiedEventFilter) {
      this.filters.push(this.copiedEventFilter);
    }

    this.locationService.getZones().subscribe((res) => {
      this.zones = res;
      this.initNewIncident();
      this.setZonesAndAreas();

      this.loading = false;
      this.changeDetectorRef.detectChanges();
    });

    this.incidentForm = this.fb.group({
      title: [
        this.quickAddTitle.length ? this.quickAddTitle : '',
        {validators: [Validators.required, this.wtValidators.textInput(3, 250, WtValidators.RESTRICTED_CHARS.DEFAULT)]},
      ],
    });

    this.subscriptions.add(
      this.incidentForm.valueChanges.subscribe((res) => {
        this.isTouched = true;
      })
    );

    this.allowedFilters = this.allowedFiltersService.getCachedAllAllowedFilters();

    this.currentEmployee = this.authenticationService.getCurrentEmployee();
    this.currentAccount = this.cachingService.GetAccountInfo();
    this.localiseItems();
    this.areaFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Area);
    this.zoneFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Zone);
    this.privacyStatus = this.privacyStatuses.find((x) => x.key === PrivacyStatuses.Open);

    if (this.incidentSubType === IncidentSubTypes.Job) {
      this.showDueDateAndStartDate = !this.showDueDateAndStartDate;
      this.showPrivacyStatus = !this.showPrivacyStatus;
    }

    this.subscriptions.add(
      this.documentCategoryService.getDocumentCategories().subscribe((res) => (this.documentCategories = res))
    );

    this.subscriptions.add(
      this.accountSettingsService.getUseDefaultLocationSetting().subscribe((res) => {
        this.useDefaultLocation = res;
        this.accountSettingsService.getDefaultLocation().subscribe((res) => {
          this.defaultLocation = res;
          if (this.useDefaultLocation && !this.usePopulatedIncident) {
            this.incident.latitude = this.defaultLocation.latitude;
            this.incident.longitude = this.defaultLocation.longitude;
            this.incident.locationDetails = this.defaultLocation.locationDetails;
          }
        });
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get incidentChannelId() {
    return this.incidentsManager.getIncidentChannelId();
  }

  onDropdownStateChanged(dropdownState: IDropdownState, filterType: FilterTypes) {
    if (!dropdownState.isToggled) {
      if (filterType) {
        return (this.activeEditAreaByFilterTypes[filterType] = false);
      }

      return;
    }
    // debugger

    Object.keys(this.activeEditAreaByFilterTypes)
      .map((k) => +k as FilterTypes)
      .filter((k) => k !== filterType)
      .forEach((k) => (this.activeEditAreaByFilterTypes[k] = false));

    if (filterType) {
      this.activeEditAreaByFilterTypes[filterType] = true;
    }

    this.changeDetectorRef.markForCheck();

    let element: HTMLElement = dropdownState.wrapper;

    while (element) {
      if (element.offsetTop) {
        break;
      }

      element = element.parentElement;
    }
  }

  isTheFormValid() {
    return this.incidentForm.valid && !this.invalidDescription;
  }

  initNewIncident() {
    this.incident = new IncidentDetailsItemViewModel();
    this.currentEmployee = this.authenticationService.getCurrentEmployee();
    this.incident.title = this.quickAddTitle.length ? this.quickAddTitle : '';
    this.incident.description = '';
    this.incident.reportedBy = '';
    this.incident.accountId = this.currentEmployee.accountId;
    this.incident.createdBy = new EmployeeViewModel(
      this.currentEmployee.id,
      this.currentEmployee.firstName,
      this.currentEmployee.surname,
      0
    );
    this.incident.severity = this.severityCheckpoint.severity;
    this.incident.filters = [];
    this.incident.privacyStatus = PrivacyStatuses.Open;
    this.incident.filters.push(
      this.addFilter(
        FilterTypes.Privacy_Status,
        PrivacyStatuses.Open.toString(),
        this.localisationService.localiseFilterValueByFilterType(PrivacyStatuses.Open, FilterTypes.Privacy_Status)
      )
    );
    this.incident.incidentItemType = IncidentItemTypes.Incident;
    this.incident.filters.push(
      this.addFilter(FilterTypes.Incident_Type, IncidentItemTypes.Incident.toString(), this.localisedIncident)
    );
    this.incident.type = this.incidentSubType;
    this.incident.filters.push(
      this.addFilter(
        FilterTypes.Incident_Severity,
        this.severityCheckpoint.severity.toString(),
        this.severityCheckpoint.description
      )
    );
    if (this.currentAccount.fixedFilters === FixedFilters.Event) {
      this.incident.eventId = Number(this.cachingService.selectedEventID) || 0;
      this.incident.filters.push(this.addFilter(FilterTypes.Event, this.incident.eventId.toString(), ''));
    } else if (this.currentAccount.fixedFilters === FixedFilters.VenueZone) {
      const zoneId = +this.cachingService.selectedZoneID;
      if (zoneId > 0) {
        this.initialSelectedZone = this.fixedZoneService.fixedFilter;
        this.filters.push(this.initialSelectedZone);
      }
    }
    if (this.useDefaultLocation) {
      this.incident.locationDetails = 'n/a';
      this.incident.latitude = 0;
      this.incident.longitude = 0;
    } else if (this.defaultLocation) {
      this.incident.locationDetails = this.defaultLocation.locationDetails;
      this.incident.latitude = this.defaultLocation.latitude;
      this.incident.longitude = this.defaultLocation.longitude;
    }

    if (this.incidentSubType === IncidentSubTypes.Job) {
      this.start = new Date().toISOString();
      this.due = new Date().toISOString();
    }

    if (this.categoryId) {
      this.getSelectedIncidentCategory(this.categoryId);
    }

    this.incident = Object.assign({}, this.incident);
    this.incident.incidentChannelId = this.currentEmployee.defaultIncidentChannel;
    this.incident.filters.push(this.addFilter(FilterTypes.Incident_Channel, this.incidentChannelId.toString(), ''));
    this.changeDetectorRef.detectChanges();
  }

  addFilter(filterType: FilterTypes, value: unknown, text: string, dateProperty: FilterDateOptions = undefined): FilterViewModel {
    const filter = new FilterViewModel();
    filter.filterType = filterType;
    filter.filterValue = value;
    filter.filterText = text;
    filter.dateProperty = dateProperty;
    return filter;
  }

  initPreselectedZone() {
    const zoneId = +this.cachingService.selectedZoneID;
    this.initialSelectedZone = this.zoneFilters.find((x) => x.filterValue == zoneId);
    this.filters.push(this.initialSelectedZone);

    this.handleFilterChange(this.filters);
  }

  initModalData() {
    this.currentEmployee = this.authenticationService.getCurrentEmployee();
    this.filteredStatuses = this.localisationService
      .getLocalizedIncidentPrivacyStatuses()
      .filter((s) => s.key !== PrivacyStatuses.Open);
  }

  // DETAILS PAGE CHANGES
  onSeverityCheckpointSelected(severityCheckpoint: SeverityViewModel) {
    this.incident.severity = severityCheckpoint.severity;
    this.severityCheckpoint = severityCheckpoint;
  }

  onPrivacyStatusSelected(privacyStatus: { key: PrivacyStatuses; value: string }) {
    this.privacyStatus = privacyStatus;
    this.incident.privacyStatus = privacyStatus.key;
  }

  onMakeConfidentialChecked(state: boolean) {
    if (state) {
      this.incident.privacyStatus = PrivacyStatuses.Confidential;
    } else {
      this.incident.privacyStatus = PrivacyStatuses.Open;
    }
    this.showConfidentialStatuses = state;
    this.privacyStatus = this.privacyStatuses.find((s) => s.key == this.incident.privacyStatus);
    if (!state) {
      //this.searchDropdownComponent.toggleDropdown(new Event('click'));
    }
    this.changeDetectorRef.markForCheck();
  }

  //LOCATION PAGE CHANGES
  onIncidentLocationUpdated(location: LocationViewModel) {
    this.incident.locationDetails = location.locationDetails;
    this.incident.latitude = location.latitude;
    this.incident.longitude = location.longitude;
  }

  // UPLOADS PAGE CHANGES
  updateUploadViewModels(uploadViewModels: UploadViewModel[]) {
    this.uploadDocumentViewModels = [];
    this.uploadDocumentViewModels = uploadViewModels;
  }


  get severityColor(): string {
    return this.incidentsManager.getColorBySeverity(this.incident.severity);
  }


  onSaveAndExit() {
    this.validationErrors = [];
    if (!this.incidentForm.valid || this.invalidDescription) {
      this.triggerErrors = true;
      return;
    }

    this.incident.status = IncidentStatuses.Open;
    this.incident.filters.push(this.addFilter(FilterTypes.Incident_Status, IncidentStatuses.Open.toString(), ''));
    this.addIncident();
  }

  addIncident() {
    this.loading = true;

    //Added to make sure the Event association works correctly for Quick Incidents and PIRs
    const preselectedEventFilter = this.filters.find(f => f.filterType === this.filterTypes.Event);
    if(preselectedEventFilter) this.incident.eventId = +preselectedEventFilter.filterValue;

    if(this.isCalledFromPIR && !this.copyEventFromPIR && this.copiedEventFilter) {
      const filtersWithoutEventFilter = this.filters.filter(f => f.filterType !== this.filterTypes.Event);
      this.filters = filtersWithoutEventFilter;
      this.incident.eventId = null;
    }

    this.incident.filters = this.filters;

    if (this.incidentForm.valid) {
      this.incident.title = this.incidentForm.controls.title.value.trim() as string;
    }

    this.incident.filters.push(this.addFilter(FilterTypes.Date, this.due, '', FilterDateOptions.Due_Date));
    this.incident.filters.push(this.addFilter(FilterTypes.Incident_Status, IncidentStatuses.Open.toString(), ''));

    if (!this.incident.startTime) {
      this.incident.startTime = moment().toISOString();
      this.incident.filters.push(this.addFilter(FilterTypes.Date, moment().toString(), '', FilterDateOptions.Start_Date));
    } else {
      this.incident.filters.push(this.addFilter(FilterTypes.Date, this.start, '', FilterDateOptions.Start_Date));
    }

    this.incidentsService.add(this.incident).subscribe((validateModel) => {
      const errors = validateModel.errorList;
      if (errors?.length > 0) {
        this.validationErrors = errors;
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      } else {
        void this.alertService.success(this.translateService.instant(T.common.item_was_added_successfully, { item: this.localisedIncident }));
        this.incident = validateModel.returnModel as IncidentDetailsItemViewModel;
        this.itemCreated.next({item: validateModel.returnModel as IncidentDetailsItemViewModel, objectType: this.objectType});

        this.uploadDocumentViewModels.forEach((element) => {
          element.globalObjectId = this.incident.id;
        });

        this.documentService.addDocuments(this.uploadDocumentViewModels).subscribe(() => {
          this.loading = false;

          const model = validateModel.returnModel as IncidentDetailsItemViewModel;
          this.objectEventEmitters.broadcastObjectAdded(model.id, this.objectType, model);
          this.onClose();
          this.changeDetectorRef.detectChanges();
        });
      }
    });
  }


  onCancel() {
    this.clearUploads();
    this.bsModalRef.hide();
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'escape' }));
  }

  private clearUploads() {
    if (this.uploadDocumentViewModels && this.uploadDocumentViewModels.length) {
      this.uploadDocumentViewModels.forEach((uploadModel) => {
        if (uploadModel.uploadType === UploadTypes.File) {
          this.subscriptions.add(this.documentService.deleteUnassignedFile(uploadModel.fileName).subscribe());
        }
      });
    }
  }

  confirmClose() {
    this.confirmationService.confirmThis(
      this.translateService.instant(T.common.are_sure_you_want_to_leave_page),
      () => {
        this.onClose();
      },
      () => {
        //handle cancel
      },
      this.translateService.instant(T.common.confirm),
      false,
      this.translateService.instant(T.common.stay),
      this.translateService.instant(T.common.leave),
      'danger'
    );

  }
  onClose() {
    this.clearUploads();
    this.closed.emit();
    return this.bsModalRef.hide();
  }

  onViewIncidentDetails() {
    void this.router.navigate([`/${this.currentAccount.accountUID}/control/list/` + this.incident.id.toString()]);
    this.onClose();
  }

  // SUMMARY METHODS

  get incidentCategorySummary(): string {
    const matchingFilters = this.filters.filter((f) => f.filterType === FilterTypes.Incident_Category);
    return matchingFilters ? matchingFilters.map((l) => l.filterText).join(', ') : '';
  }

  get incidentTagSummary(): string {
    const matchingFilters = this.filters.filter((f) => f.filterType === FilterTypes.Tag);
    return matchingFilters ? matchingFilters.map((l) => l.filterText).join(', ') : '';
  }
  get incidentPrivacyString(): string {
    if (isNaN(this.incident.privacyStatus)) {
      return 'n/a';
    }

    return this.privacyStatuses.find((e) => e.key === this.incident.privacyStatus).value;
  }

  get incidentDepartmentSummary(): string {
    const matchingFilters = this.filters.filter((f) => f.filterType === FilterTypes.Department);
    return matchingFilters ? matchingFilters.map((l) => l.filterText).join(', ') : '';
  }

  get incidentOwnerSummary(): string {
    const matchingFilters = this.filters.filter((f) => f.filterType === FilterTypes.Owner);
    return matchingFilters ? matchingFilters.map((l) => l.filterText).join(', ') : '';
  }

  onEndDateChanged(dateAsIsoString: string) {
    this.due = dateAsIsoString;
  }

  onStartDateChanged(dateAsIsoString: string) {
    this.start = dateAsIsoString;
  }

  onStartTimeChanged(dateAsObj: string) {
    this.incident.startTime = dateAsObj;
  }

  onEndTimeChanged(dateAsObj: string) {
    this.incident.endTime = dateAsObj;
  }

  onShowPickersChanged(state: boolean) {
    if (!state) {
      this.incident.endTime = undefined;
      this.incident.startTime = undefined;
    } else {
      this.incident.startTime = moment().set("hour", 7).set("minute", 0).toISOString();
      this.onStartDateChanged(this.incident.startTime);
    }
    this.showPickers = state;
  }

  public addNewExternalUser(): void {
    let modalConfig: ModalOptions<ManageExternalUserComponent> = {
      backdrop: 'static',
      keyboard: false,
      class: 'modal-right-end-full-height-add',
    };
    const initialState = {
      editMode: false,
      employeeDetailsViewModel: null,
      useOptions: true,
      currentlySelectedExternalUserId: this.currentlySelectedExternalUser?.filterValue
    };

    const modalParams = Object.assign({}, modalConfig, { initialState });
    const bsModalRef = this.modalService.show(ManageExternalUserComponent, modalParams);
    bsModalRef.content.onAddExistingUser.subscribe((filters: FilterViewModel[]) => {
      const filtersWithoutExternalUsers = this.filters.filter(f => f.filterType !== FilterTypes.External_User);
      const mergedFilters = [...filtersWithoutExternalUsers, ...filters];
      this.currentlySelectedExternalUser = filters.length > 0 ? filters[0] : null;
      this.handleFilterChange(mergedFilters, FilterTypes.External_User);
      this.changeDetectorRef.markForCheck();
    });
    bsModalRef.content.onCreateAndAddExternalUser.subscribe((data: EmployeeDetailsViewModel) => {
      const externalUserFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.External_User);
      const newExternalUserFilter = externalUserFilters.find(f => f.filterValue === data.id);
      const filtersWithoutExternalUsers = this.filters.filter(f => f.filterType !== FilterTypes.External_User);
      const mergedFilters = [...filtersWithoutExternalUsers, newExternalUserFilter];
      this.currentlySelectedExternalUser = newExternalUserFilter;
      this.handleFilterChange(mergedFilters, FilterTypes.External_User);
      this.changeDetectorRef.markForCheck();
    });
  }

  handleFilterChange(filters: FilterViewModel[], filterType: FilterTypes = null) {
    let newAppliedFilters = [...filters];

    if (filterType && filterType === FilterTypes.Incident_Category) {
      const categoryId = filters?.find(x => x.filterType === FilterTypes.Incident_Category)?.filterValue as number;

      if(categoryId > 0) {
        this.getSelectedIncidentCategory(categoryId);
      } else {
        newAppliedFilters = this.removeSelectedAssociatedCategoryFilters(filters);
      }
    }

    this.selectedZone = filters.find((f) => f.filterType === FilterTypes.Zone);
    this.filters = newAppliedFilters;

    if (this.filters.some(f => f.filterType === FilterTypes.External_User)) {
      this.currentlySelectedExternalUser = this.filters?.find(x => x.filterType === FilterTypes.External_User);
    } else if (filterType === FilterTypes.External_User) {
      this.currentlySelectedExternalUser = null;
    }
    this.setZonesAndAreas();

    if (filters.length > 1) {
      this.isTouched = true;
    }

    this.changeDetectorRef.detectChanges();
  }

  getFiltersByType(filterType: FilterTypes): FilterViewModel[] {
    let result: FilterViewModel[] = [];

    const matchingFilters = this.filters.filter((f) => f.filterType === filterType);

    if (matchingFilters) {
      result = matchingFilters;
    }

    return result;
  }

  getFilterValuesByFilterType(filterType: FilterTypes): string {
    const matchingFilterValues = this.filters.filter((f) => f.filterType === filterType).map((x) => x.filterText);

    return matchingFilterValues.join('; ');
  }

  setInitialZonePin() {
    this.incident = LocationUtilities.setLocationDetails(this.mapZones, this.incident) as IncidentDetailsItemViewModel;

    this.changeDetectorRef.detectChanges();
  }

  setZonesAndAreas() {
    this.mapZones = this.filterToViewModelUtilityService.getZoneViewModelsFromFilters(this.filters, this.zones);
    this.mapAreas = this.filterToViewModelUtilityService.getAreaViewModelsFromFilters(this.filters);

    this.setApplicableFilters();
    this.changeDetectorRef.markForCheck();
  }

  private setApplicableFilters() {
    if (this.mapZones && this.mapZones.length) {
      const areas: FilterViewModel[] = [];
      this.mapZones.forEach((zone) => {
        const zoneAreas = this.areaFilters.filter((a) => +a.relatedObjectId === zone.id);

        if (zoneAreas) {
          areas.push(...zoneAreas);
        }
      });
      this.applicableAreaFilters = areas.slice();

      this.filters.forEach((filter) => {
        if (filter.filterType === FilterTypes.Area) {
          if (!this.mapZones.some((zone) => zone.id === filter.relatedObjectId)) {
            this.filters = this.filters.filter((x) => x.filterValue != filter.filterValue);
          }
        }
      });

      if (!this.incident.latitude && !this.incident.longitude && this.filters.some((x) => x.filterType === FilterTypes.Zone)) {
        this.setInitialZonePin();
      }
    } else {
      this.mapAreas = [];
      this.applicableAreaFilters = [];
      this.filters = this.filters.filter((x) => x.filterType !== FilterTypes.Area);
    }
  }

  private localiseItems() {
    this.localisedIncident = this.localisationService.localiseObjectType(ObjectTypes.IncidentItem);
    this.localisedOwners = this.localisationService.localiseFilterType(FilterTypes.Owner, true);
    this.localisedDepartments = this.localisationService.localiseFilterType(FilterTypes.Department, true);
    this.localisedZones = this.localisationService.localiseObjectType(ObjectTypes.Zone, true);
    this.localisedAreas = this.localisationService.localiseObjectType(ObjectTypes.Area, true);
  }

  public getSelectedIncidentCategory(categoryId: number) {
    this.incidentCategoriesService.details(categoryId).subscribe(res => {
      this.selectedCategory = res;

      if (this.selectedCategory.severity !== 0) {
        this.incident.severity = this.selectedCategory.severity;
        this.severityCheckpoint = this.incidentsManager.getSeverityByValue(this.selectedCategory.severity);
      }

      if (this.selectedCategory.privacyStatus !== PrivacyStatuses.Open) {

        this.privacyStatus = this.privacyStatuses.find((x) => x.key === this.selectedCategory.privacyStatus);
        this.incident.privacyStatus = this.privacyStatus.key;
        this.showConfidentialStatuses = true;
      }

      const filterTypesToApply = [FilterTypes.Owner, FilterTypes.Department, FilterTypes.User_Group];

      filterTypesToApply.forEach(filterType => {
        this.filters = this.filters.filter(f => f.filterType !== filterType);

        const categoryFiltersOfType = this.selectedCategory.filters.filter(f => f.filterType === filterType);
        if (categoryFiltersOfType.length > 0) {
          this.filters.push(...categoryFiltersOfType);
        }
      });

      const descriptionFilter = this.selectedCategory.filters.find(f => f.filterType === FilterTypes.Description);
      if (descriptionFilter) {
        this.filters = this.filters.filter(f => f.filterType !== FilterTypes.Description);
        this.filters.push(descriptionFilter);
        this.incident.description = descriptionFilter.filterValue as string;
      }

      this.changeDetectorRef.detectChanges();
    })
  }

  public removeSelectedAssociatedCategoryFilters(appliedFilters: FilterViewModel[]): FilterViewModel[] {
    this.incident.severity = null;

    this.selectedCategory?.filters.forEach(f => {
      if (f.filterType === FilterTypes.Owner || f.filterType === FilterTypes.Department || f.filterType === FilterTypes.User_Group) {
        appliedFilters = appliedFilters.filter(x => x.filterValue !== f.filterValue);
      }

      if (f.filterType === FilterTypes.Description) {
        appliedFilters = appliedFilters.filter(x => x.filterValue !== f.filterValue);

        this.incident.description = null;;
      }

      if (f.filterType === FilterTypes.Incident_Category) {
        appliedFilters = appliedFilters.filter(x => x.filterType !== FilterTypes.Incident_Category);
      }
    });

    this.selectedCategory = null;
    this.preselectedCategoryId = null;

    return appliedFilters;
  }

  updateDescription(description: string) {
    this.incident.description = description;
    const filter = this.incident.filters.find((f) => f.filterType === FilterTypes.Description);

    if (filter) {
      filter.filterValue = description;
    }
  }

  public isDescriptionInvalid(disable: boolean) {
    this.invalidDescription = disable;
    this.changeDetectorRef.detectChanges();
  }

  public setMyDepartment(): void {
    if (this.allowedFilters) {
      const empFilter = this.allowedFilters.find(
        (f) => f.filterType === FilterTypes.Department && f.filterValue.toString() === this.currentEmployee.departmentId.toString()
      );

      if (empFilter && this.filters.indexOf(empFilter) < 0) {
        this.filters = this.filters.slice();
        this.filters.push(empFilter);
      }

    }
    this.changeDetectorRef.detectChanges();
  }

  setAccountForCurrentOwner() {
    const result = AssignMeAsOwnerOrApproverFilterUtilities.addCurrentUserAsOwnerOrApprover(
      this.filters,
      this.currentEmployee,
      this.allowedFilters
    );

    this.filters = result.filters;
    this.changeDetectorRef.detectChanges();
  }
}
